

/**
 * AppBarUserControls component - Client component for user-related controls in the app bar
 * Manages user settings, notifications, and profile interactions
 */
import { usePopup } from "@/components/AppBar/hooks/usePopup";
import { IconBell } from "@tabler/icons-react";
import { AiAssistantButton } from "../../AiAssistant/AiAssistantButton";
import { PNotifications } from "../../PNotifications/PNotifications";
import { PUserSettings } from "./PUserSettings";
import { Spacing } from "../../Spacing/Spacing";
import { UserIcon } from "../../UserIcon/UserIcon";
import { User } from "@/types/User";
import { UserLog } from "@/types/UserLog";
import { useNotifications } from "../hooks/useNotifications";
import { ConnectionDot } from "./ConnectionDot";

/**
 * Props for the AppBarUserControls component
 */
type AppBarUserControlsProps = {
  user: User; // User data object
  notificationsProp: UserLog[]; // Array of user logs for notifications
  notificationsUnreadProp: UserLog[]; // Array of unread user
  numUnreadProp: number; // Number of unread notifications
  fetchDate: Date; // Date of the last fetch
};

export const AppBarUserControls: React.FC<AppBarUserControlsProps> = ({
  user,
  notificationsProp,
  notificationsUnreadProp,
  numUnreadProp,
  fetchDate,
}) => {
  // Initialize popup hook with state for both popups
  const { state, avatarRef, bellRef, toggleAvatarPopup, toggleBellPopup } =
    usePopup();

  // Get unread notifications state from context
  const {
    notificationState,
    toggleNotificationView,
    handleNotificationUpdate,
    markAllNotificationsAsRead,
    markNotificationAsRead,
    fetchAdditionalNotifications,
    fetchAdditionalUnreadNotifications,
  } = useNotifications(
    numUnreadProp,
    notificationsProp,
    notificationsUnreadProp,
    fetchDate
  );

  return (
    <div className="relative flex flex-col flex-1">
      <div className="flex flex-row items-center justify-end">
        {/* Connection dot: amber syncing / red offline or unreachable, and
            nothing while live. Leftmost on purpose: the group is right-
            aligned, so a dot that popped in between ✨ and the bell would
            shove ✨ 24px left on every slow save; out here nothing moves.
            `mr-2` mirrors the AI button's own wrapper, so the gap to the
            next icon is the same whether ✨ renders or not. Also rendered
            in AppBarStripped, so an outage that swaps this whole bar for
            the error strip cannot hide it. */}
        <ConnectionDot className="mr-2" />
        {/* AI assistant entry sits left of the bell so the icon row reads
            [✨][bell][avatar]; renders null during onboarding/immersive routes. */}
        <AiAssistantButton />
        <div className="relative inline-flex items-center justify-center">
          <IconBell
            onClick={toggleBellPopup}
            size={20}
            className="font-secondary cursor-pointer"
          />

          {/* Improved notification indicator positioning */}
          {notificationState.numUnread > 0 && (
            <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full surface-font-success body-4-regular font-light-everytime">
              {notificationState.numUnread > 9
                ? "9+"
                : notificationState.numUnread}
            </span>
          )}
        </div>
        <Spacing width="w-2" />
        <UserIcon
          onClickAction={toggleAvatarPopup}
          initialArgs={user?.firstName?.charAt(0)}
          monogramColorArgs={user?.monogramColor}
          testId="appbar-user-menu"
        />

        {/* Popups */}
        {state.isOpenBell && (
          <PNotifications
            ref={bellRef}
            notificationState={notificationState}
            toggleNotificationViewAction={toggleNotificationView}
            handleNotificationUpdateAction={handleNotificationUpdate}
            markAllNotificationsAsReadAction={markAllNotificationsAsRead}
            markNotificationAsReadAction={markNotificationAsRead}
            fetchAdditionalNotificationsAction={fetchAdditionalNotifications}
            fetchAdditionalUnreadNotificationsAction={
              fetchAdditionalUnreadNotifications
            }
            onNavigateAction={toggleBellPopup}
          />
        )}
        {state.isOpenAvatar && (
          <PUserSettings
            onClose={toggleAvatarPopup}
            ref={avatarRef}
            user={user}
          />
        )}
      </div>
    </div>
  );
};
